import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import api from '@/lib/api';
import { 
  Plus, Search, Edit, Trash2, Bus, Phone, User, 
  Users, Loader2, CheckCircle, Play
} from 'lucide-react';

export default function DnaGoVans() {
  const [vans, setVans] = useState([]);
  const [alunos, setAlunos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [retirandoVan, setRetirandoVan] = useState(null);
  
  const [formData, setFormData] = useState({
    placa: '',
    motorista_nome: '',
    motorista_telefone: '',
    monitor_nome: '',
    monitor_telefone: '',
    alunos_ids: [],
    observacoes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [vansRes, alunosRes] = await Promise.all([
        api.get('/dnago/vans'),
        api.get('/students')
      ]);
      setVans(vansRes.data || []);
      setAlunos(alunosRes.data || []);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.placa.trim() || !formData.motorista_nome.trim()) {
      toast.error('Placa e nome do motorista são obrigatórios');
      return;
    }

    setSaving(true);
    try {
      const params = new URLSearchParams();
      params.append('placa', formData.placa);
      params.append('motorista_nome', formData.motorista_nome);
      if (formData.motorista_telefone) params.append('motorista_telefone', formData.motorista_telefone);
      if (formData.monitor_nome) params.append('monitor_nome', formData.monitor_nome);
      if (formData.monitor_telefone) params.append('monitor_telefone', formData.monitor_telefone);
      if (formData.observacoes) params.append('observacoes', formData.observacoes);
      formData.alunos_ids.forEach(id => params.append('alunos_ids', id));

      if (editingId) {
        await api.put(`/dnago/vans/${editingId}?${params.toString()}`);
        toast.success('Van atualizada!');
      } else {
        await api.post(`/dnago/vans?${params.toString()}`);
        toast.success('Van cadastrada!');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (van) => {
    setEditingId(van.id);
    setFormData({
      placa: van.placa || '',
      motorista_nome: van.motorista_nome || '',
      motorista_telefone: van.motorista_telefone || '',
      monitor_nome: van.monitor_nome || '',
      monitor_telefone: van.monitor_telefone || '',
      alunos_ids: van.alunos_ids || [],
      observacoes: van.observacoes || ''
    });
    setDialogOpen(true);
  };

  const handleRetiradaLote = async (vanId) => {
    setRetirandoVan(vanId);
    try {
      const res = await api.post(`/dnago/vans/${vanId}/retirada-lote`);
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro na retirada em lote');
    } finally {
      setRetirandoVan(null);
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData({
      placa: '',
      motorista_nome: '',
      motorista_telefone: '',
      monitor_nome: '',
      monitor_telefone: '',
      alunos_ids: [],
      observacoes: ''
    });
  };

  const getAlunoNome = (id) => {
    const aluno = alunos.find(a => a.id === id);
    return aluno?.name || 'N/A';
  };

  const filteredVans = vans.filter(v =>
    v.placa?.toLowerCase().includes(search.toLowerCase()) ||
    v.motorista_nome?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Bus className="w-7 h-7 text-purple-600" />
            Vans Escolares
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Cadastro e controle de vans e transporte escolar
          </p>
        </div>
        <Button 
          onClick={() => { resetForm(); setDialogOpen(true); }}
          className="bg-purple-600 hover:bg-purple-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nova Van
        </Button>
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Bus className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-700">{vans.length}</p>
                <p className="text-xs text-gray-500">Total de Vans</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-white border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-700">
                  {vans.filter(v => v.ativo !== false).length}
                </p>
                <p className="text-xs text-gray-500">Ativas</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-50 to-white border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Users className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-purple-700">
                  {vans.reduce((sum, v) => sum + (v.alunos_ids?.length || 0), 0)}
                </p>
                <p className="text-xs text-gray-500">Alunos Transportados</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Busca */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Buscar por placa ou motorista..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Grid de Vans */}
      {filteredVans.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-gray-500">
            <Bus className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-lg">Nenhuma van cadastrada</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredVans.map((van) => (
            <Card key={van.id} className="hover:shadow-lg transition-shadow">
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Bus className="w-5 h-5 text-blue-600" />
                      {van.placa}
                    </CardTitle>
                    <CardDescription>{van.motorista_nome}</CardDescription>
                  </div>
                  {van.ativo !== false ? (
                    <Badge className="bg-green-100 text-green-700">Ativa</Badge>
                  ) : (
                    <Badge variant="secondary">Inativa</Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center gap-1 text-gray-600">
                    <Phone className="w-3 h-3" />
                    {van.motorista_telefone || '-'}
                  </div>
                  <div className="flex items-center gap-1 text-gray-600">
                    <Users className="w-3 h-3" />
                    {van.alunos_ids?.length || 0} alunos
                  </div>
                </div>

                {van.monitor_nome && (
                  <div className="text-sm text-gray-500">
                    <User className="w-3 h-3 inline mr-1" />
                    Monitor: {van.monitor_nome}
                  </div>
                )}

                {van.alunos_ids?.length > 0 && (
                  <div className="text-xs text-gray-400 truncate">
                    {van.alunos_ids.slice(0, 3).map(id => getAlunoNome(id)).join(', ')}
                    {van.alunos_ids.length > 3 && ` +${van.alunos_ids.length - 3}`}
                  </div>
                )}

                <div className="flex gap-2 pt-2">
                  <Button
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    size="sm"
                    onClick={() => handleRetiradaLote(van.id)}
                    disabled={retirandoVan === van.id || !van.alunos_ids?.length}
                  >
                    {retirandoVan === van.id ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-1" />
                    ) : (
                      <Play className="w-4 h-4 mr-1" />
                    )}
                    Retirada em Lote
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleEdit(van)}>
                    <Edit className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog Cadastro/Edição */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bus className="w-5 h-5 text-blue-600" />
              {editingId ? 'Editar Van' : 'Nova Van'}
            </DialogTitle>
            <DialogDescription>
              Cadastre os dados da van e vincule os alunos transportados
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Placa *</Label>
                <Input
                  value={formData.placa}
                  onChange={(e) => setFormData({ ...formData, placa: e.target.value.toUpperCase() })}
                  placeholder="ABC-1234"
                  required
                />
              </div>

              <div>
                <Label>Motorista *</Label>
                <Input
                  value={formData.motorista_nome}
                  onChange={(e) => setFormData({ ...formData, motorista_nome: e.target.value })}
                  placeholder="Nome do motorista"
                  required
                />
              </div>

              <div>
                <Label>Telefone Motorista</Label>
                <Input
                  value={formData.motorista_telefone}
                  onChange={(e) => setFormData({ ...formData, motorista_telefone: e.target.value })}
                  placeholder="(00) 00000-0000"
                />
              </div>

              <div>
                <Label>Monitor</Label>
                <Input
                  value={formData.monitor_nome}
                  onChange={(e) => setFormData({ ...formData, monitor_nome: e.target.value })}
                  placeholder="Nome do monitor"
                />
              </div>

              <div>
                <Label>Telefone Monitor</Label>
                <Input
                  value={formData.monitor_telefone}
                  onChange={(e) => setFormData({ ...formData, monitor_telefone: e.target.value })}
                  placeholder="(00) 00000-0000"
                />
              </div>

              <div className="md:col-span-2">
                <Label>Alunos Transportados</Label>
                <select
                  multiple
                  value={formData.alunos_ids}
                  onChange={(e) => {
                    const selected = Array.from(e.target.selectedOptions, option => option.value);
                    setFormData({ ...formData, alunos_ids: selected });
                  }}
                  className="w-full h-40 border rounded-md p-2"
                >
                  {alunos.map((aluno) => (
                    <option key={aluno.id} value={aluno.id}>
                      {aluno.name} - {aluno.class_name || 'Sem turma'}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Segure Ctrl/Cmd para selecionar múltiplos alunos
                </p>
              </div>

              <div className="md:col-span-2">
                <Label>Observações</Label>
                <Textarea
                  value={formData.observacoes}
                  onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                  placeholder="Observações sobre a van..."
                  rows={2}
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingId ? 'Salvar' : 'Cadastrar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
